// Anonymised "what people are asking" ticker — powered by real community_questions
// submissions. Shows community + question text only (no emails, no PII).
import { useEffect, useState } from "react";
import { MessageSquare } from "lucide-react";
import { fetchTopQuestions } from "@/lib/platformApi";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "2-digit" }); }
  catch { return iso.slice(0, 10); }
};

export default function CommunityQuestionsTicker({ testid = "questions-ticker" }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchTopQuestions(8)
      .then((d) => { if (!cancelled) setItems(d.items || []); })
      .catch(() => { /* silent — homepage shouldn't break if API is cold */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // Don't render until we know whether there's content — and never render
  // if there are zero real submissions (no fabricated placeholders).
  if (!loaded || items.length === 0) return null;

  return (
    <section data-testid={testid} className="bg-[#F8F9FA] border-y border-slate-200 py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 mb-3 flex items-center gap-2">
              <MessageSquare size={14} className="text-[#FF8A00]" />
              What communities are asking
            </div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-[#0A1224] leading-tight">
              Real questions. Real replies. <span className="text-[#FF8A00]">No marketing list.</span>
            </h2>
          </div>
          <p className="text-sm text-slate-500 max-w-md leading-relaxed">
            Each question came in through a form on this site. Emails are kept private;
            the question text is shown so the next visitor can see what their peers are
            asking — and add their own.
          </p>
        </div>

        <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200" data-testid={`${testid}-list`}>
          {items.map((q, i) => (
            <li
              key={i}
              data-testid={`${testid}-item-${i}`}
              className="bg-white p-6 hover:border-l-4 hover:border-[#FF8A00] hover:pl-5 transition-all border-l-4 border-transparent"
            >
              <div className="flex items-baseline justify-between gap-3 mb-3">
                <div className="font-bold text-[#0A1224] tracking-tight">{q.community}</div>
                <div className="text-xs text-slate-400 tabular-nums">{fmtDate(q.created_at)}</div>
              </div>
              <p className="text-slate-700 italic leading-relaxed text-sm">
                "{q.question}"
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
